import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import {
  CalendarOptions,
  DateSelectArg,
  DateSpanApi,
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventInput,
  SlotLabelContentArg,
} from '@fullcalendar/core';
import { EventImpl } from '@fullcalendar/core/internal';
import interactionPlugin, {
  DateClickArg,
  DropArg,
} from '@fullcalendar/interaction';
import { ResourceInput, ResourceLabelContentArg } from '@fullcalendar/resource';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { environment } from '@siat-fe/env';
import { take } from 'rxjs';
import { Item } from '../../form/select-field/select-field.component';
import {
  formatTurnoTimeRange,
  getMoreLinkLabel,
  isAppuntamentoUnresolved,
  isSameDay,
  renderDayHeaderHtml,
} from '../calendar.utils';
import { QuickAddMenuComponent } from '../components/quick-add-menu.component';
import {
  CustomCalendarOptions,
  QuickAddMenuOption,
  ResourceSlotQuickAddEvent,
} from '../models/calendar.model';
import { HolidaysService } from '../services/holidays.service';

/**
 * Emitted when an "attività non assegnata" is dropped onto a collaboratore's
 * row/day — the caller resolves it into a real `AppuntamentoRequest`.
 */
export interface ResourceTimelineAssignment {
  prestazioneId: string;
  resourceId: string;
  date: string;
}

@Component({
  selector: 'siat-calendar-timeline',
  imports: [FullCalendarModule, QuickAddMenuComponent],
  templateUrl: './calendar-timeline.component.html',
  styleUrl: './calendar-timeline.component.scss',
})
export class CalendarTimelineComponent {
  private readonly holidaysService = inject(HolidaysService);

  /**
   * Emitted when an "attività non assegnata" is dropped onto a collaboratore's
   * row/day — the caller resolves it into a real `AppuntamentoRequest`.
   */
  readonly externalDrop = output<{
    activityId: string;
    role: string | null;
    resourceId: string;
    date: Date;
  }>();

  private readonly hostEl = inject(ElementRef).nativeElement as HTMLElement;

  /**
   * Custom drop-target highlight (v. `externalDrop`): FullCalendar's own
   * `.fc-highlight` never appears for this fallback, since it's only drawn
   * on a successful native hit-test — which never happens here. Positioned
   * imperatively (not Angular-templated) since it must track the raw mouse
   * position on every `mousemove`, outside change detection.
   */
  private readonly dropHighlightEl = this.createDropHighlightEl();

  private createDropHighlightEl(): HTMLDivElement {
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'fixed',
      display: 'none',
      pointerEvents: 'none',
      zIndex: '9998', // just under FullCalendar's own mirror (9999)
      boxSizing: 'border-box',
      border: '2px dashed #294a63',
      backgroundColor: 'rgba(41, 74, 99, 0.16)',
      borderRadius: '6px',
    });
    document.body.appendChild(el);
    return el;
  }

  private readonly handleGlobalMouseMove = (ev: MouseEvent) => {
    if (!document.querySelector('.fc-event-dragging')) {
      this.dropHighlightEl.style.display = 'none';
      return;
    }
    const rowEl = this.findRowElAtY(ev.clientY);
    const dateCellEl = this.findDateCellAtX(ev.clientX);
    if (!rowEl || !dateCellEl) {
      this.dropHighlightEl.style.display = 'none';
      return;
    }
    const rowRect = rowEl.getBoundingClientRect();
    const dateRect = dateCellEl.getBoundingClientRect();
    Object.assign(this.dropHighlightEl.style, {
      display: 'block',
      left: `${dateRect.left}px`,
      width: `${dateRect.width}px`,
      top: `${rowRect.top}px`,
      height: `${rowRect.height}px`,
    });
  };

  private readonly handleGlobalMouseUp = (ev: MouseEvent) => {
    this.dropHighlightEl.style.display = 'none';
    const mirror = document.querySelector('.fc-event-dragging');
    if (!mirror?.id) return;

    const resourceEl = this.findRowElAtY(ev.clientY);
    const dateStr = resourceEl
      ? this.findDateCellAtX(ev.clientX)?.getAttribute('data-date')
      : null;
    const resourceId = resourceEl?.getAttribute('data-resource-id');
    if (!resourceId || !dateStr) return;

    this.externalDrop.emit({
      activityId: mirror.id,
      role: mirror.getAttribute('role'),
      resourceId,
      date: new Date(dateStr),
    });
  };

  /**
   * Finds the resource row (`.fc-timeline-lane[data-resource-id]`) whose
   * vertical bounds contain `clientY`.
   */
  private findRowElAtY(clientY: number): HTMLElement | null {
    const rows = Array.from(
      this.hostEl.querySelectorAll<HTMLElement>(
        '.fc-timeline-lane[data-resource-id]'
      )
    );
    for (const row of rows) {
      const rect = row.getBoundingClientRect();
      if (clientY >= rect.top && clientY <= rect.bottom) return row;
    }
    return null;
  }

  /**
   * Finds the day column cell (via the stable header labels, one per day)
   * whose horizontal bounds contain `clientX`.
   */
  private findDateCellAtX(clientX: number): HTMLElement | null {
    const cells = Array.from(
      this.hostEl.querySelectorAll<HTMLElement>(
        '.fc-timeline-slot-label[data-date]'
      )
    );
    for (const cell of cells) {
      const rect = cell.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) return cell;
    }
    return null;
  }

  constructor() {
    document.addEventListener('mousemove', this.handleGlobalMouseMove);
    document.addEventListener('mouseup', this.handleGlobalMouseUp);
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('mousemove', this.handleGlobalMouseMove);
      document.removeEventListener('mouseup', this.handleGlobalMouseUp);
      this.dropHighlightEl.remove();
    });
  }

  /**
   * The collaboratori shown as resource rows.
   */
  readonly collaboratoriItems = input<Item[]>([]);

  /**
   * The events shown in the timeline.
   */
  readonly events = input<EventInput[]>([]);

  /**
   * Which view is active?
   */
  readonly activeView = input<string>('resourceTimelineMonth');

  /**
   * Any custom options to apply to the calendar.
   */
  readonly customOptions = input<CustomCalendarOptions>();

  /**
   * The calendar context: "SANITARIO" or "AMBULATORIO".
   */
  readonly calendarContext = input<'SANITARIO' | 'AMBULATORIO'>('SANITARIO');

  /**
   * If true, show holidays on the calendar.
   */
  readonly showHolidays = input<boolean>(false);

  /*
   * Same as `CalendarComponent.holidayCitta`.
   */
  readonly holidayCitta = input<string>('TRENTO');

  /**
   * If true, show a quick-add menu for each resource row.
   */
  readonly quickAddMenuEnabled = input<boolean>(false);

  /**
   * If true, show a quick-add menu for each resource row.
   */
  readonly operatorEventTypeItems = input<Item[]>([]);

  /**
   * If true, show a quick-add menu for each resource row.
   */
  readonly slotClickEnabled = input<boolean>(false);

  /**
   * If true, allow clicks on resources to be handled by the parent component.
   */
  readonly isResourceClickAllowed = input<(collaboratoreId: string) => boolean>(
    () => true
  );

  /**
   * Fired when an "attività non assegnata" is dropped onto a collaboratore's
   * row/day.
   */
  readonly assigned = output<ResourceTimelineAssignment>();

  /**
   * Fired when an event is clicked.
   */
  readonly eventClick = output<EventImpl>();

  /**
   * Fired when a quick-add menu option is selected.
   */
  readonly quickAddAction = output<ResourceSlotQuickAddEvent>();

  /**
   * Fired when a slot is clicked.
   */
  readonly slotClick = output<{ date: Date; collaboratoreId: string }>();

  /**
   * The quick-add menu options for each resource row.
   **/
  protected readonly quickAddMenuOptions = signal<QuickAddMenuOption[]>([]);

  /**
   * The slot a quick-add menu is currently open for, if any.
   */
  private pendingQuickAddSlot: {
    date: Date;
    collaboratoreId: string;
  } | null = null;

  /**
   * Reference to the quick-add menu.
   */
  private readonly quickAddMenu = viewChild(QuickAddMenuComponent);

  /**
   * Reference to the calendar.
   */
  private readonly calendar = viewChild<FullCalendarComponent>('calendar');

  /**
   * The title of the current view.
   */
  readonly currentTitle = signal<string>('');

  /**
   * True if the timeline contains events for more than one prestazione.
   */
  readonly hasPrestazioneEvents = computed<boolean>(() =>
    (this.events() ?? []).some(
      e =>
        !!(e.extendedProps as Record<string, unknown> | undefined)?.[
          'hasMatchingPrestazione'
        ]
    )
  );

  /**
   * True if "Focus Prestazione" is enabled.
   */
  readonly focusPrestazione = signal<boolean>(false);

  /**
   * Toggles "Focus Prestazione" (see `focusPrestazione`).
   */
  toggleFocusPrestazione(): void {
    this.focusPrestazione.update(value => !value);
  }

  /**
   * Explicitly sets "Focus Prestazione" (see `focusPrestazione`).
   */
  setFocusPrestazione(value: boolean): void {
    this.focusPrestazione.set(value);
  }

  /**
   * Applies the current active view to the calendar.
   */
  private readonly applyActiveView = effect(() => {
    const view = this.activeView();
    this.calendar()?.getApi()?.changeView(view);
  });

  /**
   * Re-renders the calendar when "Focus Prestazione" is toggled.
   */
  private readonly reRenderOnFocusToggle = effect(() => {
    this.focusPrestazione();
    this.calendar()?.getApi()?.render();
  });

  /*
   * Goes to the previous period.
   */
  public prev(): void {
    this.calendar()?.getApi()?.prev();
  }

  /**
   * Goes to the next period.
   */
  public next(): void {
    this.calendar()?.getApi()?.next();
  }

  /**
   * Goes to the current period.
   */
  public goToday(): void {
    this.calendar()?.getApi()?.today();
  }

  /**
   * Goes to a specific date.
   */
  public gotoDate(date: Date): void {
    this.calendar()?.getApi()?.gotoDate(date);
  }

  /**
   * Refreshes the size of the calendar.
   */
  public refreshSize(): void {
    this.calendar()?.getApi()?.updateSize();
  }

  /**
   * Returns true if there are fewer than four resources.
   */
  private readonly hasFewResources = computed(
    () => this.collaboratoriItems().length <= 4
  );

  /**
   * Maps `collaboratoriItems` to FullCalendar `ResourceInput`s.
   */
  private readonly resources = computed<ResourceInput[]>(() =>
    this.collaboratoriItems().map((item, index) => ({
      id: String(item.id),
      title: item.name,
      groupOrder: item.isAmbulatorio ? 1 : 0,
      type: item.isAmbulatorio ? 'Ambulatori' : 'Sanitari',
      sortRank: index === 0 && !item.isAmbulatorio ? 0 : 1,
      extendedProps: { item },
    }))
  );

  /**
   * Maps `events` to FullCalendar `EventInput`s.
   */
  private readonly resourceEvents = computed<EventInput[]>(() => {
    const mapped = this.events().map(event => {
      const classNames = (event.classNames as string[] | undefined) ?? [];
      const isBackgroundOverride =
        classNames.includes('turno-event') ||
        classNames.includes('assenza-event') ||
        classNames.includes('travel-event');
      const timelinePriority = classNames.includes('appuntamento-event')
        ? 0
        : classNames.includes('riunione-event') ||
            classNames.includes('promemoria-event')
          ? 1
          : 2;
      return {
        ...event,
        resourceId:
          event.resourceId ??
          String(event.extendedProps?.['idSanitario'] ?? ''),
        extendedProps: { ...event.extendedProps, timelinePriority },
        ...(isBackgroundOverride ? { display: 'block' as const } : null),
      };
    });

    const byKey = new Map<string, EventInput>();
    for (const event of mapped) {
      const key = `${event.id}-${event.resourceId}`;
      if (!byKey.has(key) || event.allDay) {
        byKey.set(key, event);
      }
    }
    return [...byKey.values()];
  });

  /**
   * The `CalendarOptions` for the `resourceTimelineMonth` view.
   */
  readonly calendarOptions = computed<CalendarOptions>(() => {
    const {
      showTooltip: _showTooltip,
      nonClickableEventTypes: _nonClickableEventTypes,
      changeViewButton: _changeViewButton,
      addPrestazioneButton: _addPrestazioneButton,
      dateRangeSelection: _dateRangeSelection,
      addNewEventButton: _addNewEventButton,
      ...restCustomOptions
    } = this.customOptions() ?? {};

    return {
      schedulerLicenseKey: environment.fullCalendarLicenseKey,
      plugins: [resourceTimelinePlugin, interactionPlugin],
      initialView: this.activeView(),
      views: {
        resourceTimelineWeek: {
          slotDuration: { days: 1 },
        },
        resourceTimelineMonth: {
          slotMinWidth: 120,
        },
      },
      headerToolbar: false,
      height: this.hasFewResources() ? 'auto' : 'calc(100vh - 280px)',
      expandRows: this.hasFewResources(),
      locale: 'it',
      firstDay: 1,
      resourceAreaWidth: '230px',
      resourceAreaHeaderContent: 'Collaboratori',
      resourceGroupField: 'type',
      resourceOrder: 'groupOrder,sortRank,title',
      resources: this.resources(),
      events: this.resourceEvents(),
      editable: false,
      droppable: true,
      selectable: this.slotClickEnabled(),
      selectMirror: true,
      unselectAuto: true,
      select: (arg: DateSelectArg) => this.handleSelect(arg),
      selectAllow: (span: DateSpanApi) => this.isSelectAllowed(span),
      nowIndicator: false,
      eventMaxStack: this.hasFewResources() ? undefined : 2,
      eventOrder: 'timelinePriority',
      dayPopoverFormat: { weekday: 'long', day: 'numeric', month: 'long' },
      moreLinkClick: 'popover',
      moreLinkContent: arg => `+${arg.num} ${getMoreLinkLabel('it')}`,
      eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      slotLabelContent: (arg: SlotLabelContentArg) =>
        renderDayHeaderHtml(
          arg.date,
          'it',
          this.holidaysService.isHoliday(arg.date)
        ),
      slotLabelClassNames: arg =>
        this.holidaysService.isHoliday(arg.date) ? ['is-holiday'] : [],
      slotLaneClassNames: arg =>
        this.holidaysService.isHoliday(arg.date) ? ['is-holiday'] : [],
      resourceLaneClassNames: arg =>
        this.slotClickEnabled() &&
        !this.isResourceClickAllowed()(arg.resource.id)
          ? ['fc-lane-locked']
          : [],
      resourceLaneDidMount: arg => {
        const isLocked =
          this.slotClickEnabled() &&
          !this.isResourceClickAllowed()(arg.resource.id);
        arg.el.title = isLocked
          ? 'La prestazione è già assegnata a un altro sanitario: qui non puoi aggiungere appuntamenti.'
          : '';
        arg.el.style.cursor = isLocked
          ? 'not-allowed'
          : this.slotClickEnabled()
            ? 'pointer'
            : '';
      },
      resourceLabelContent: (arg: ResourceLabelContentArg) =>
        this.buildResourceLabel(arg),
      eventContent: (arg: EventContentArg) => this.buildEventContent(arg),
      eventClick: (arg: EventClickArg) => this.handleEventClick(arg),
      dateClick: (arg: DateClickArg) => this.handleDateClick(arg),
      drop: (info: DropArg) => this.onDrop(info),
      datesSet: (info: DatesSetArg) => {
        this.currentTitle.set(info.view.title);
        this.handleDatesSet(info);
      },
      ...restCustomOptions,
    };
  });

  /**
   * Handle event click on calendar (DEFAULT BEHAVIOR).
   * Open a MatDialog.
   * @param arg The event click data by FullCalendar.
   */
  private handleEventClick(arg: EventClickArg): void {
    if (arg.event.groupId === 'disponibilita-macchinario') return;
    if (arg.event.display === 'background') return;
    this.eventClick.emit(arg.event);
  }

  /**
   * Check if a slot click is allowed.
   * @param date The date of the clicked slot.
   * @param resourceId The resource id of the clicked slot.
   */
  private isSlotAllowed(date: Date, resourceId: string | undefined): boolean {
    if (!resourceId) return false;
    const now = new Date();
    const isPastDay = !isSameDay(date, now) && date < now;
    if (isPastDay) return false;
    if (
      this.holidaysService.blockHolidayInsert() &&
      this.holidaysService.isHoliday(date)
    ) {
      return false;
    }
    return this.isResourceClickAllowed()(resourceId);
  }

  /**
   * Handle date click on calendar (DEFAULT BEHAVIOR).
   */
  private handleDateClick(arg: DateClickArg): void {
    if (!this.quickAddMenuEnabled()) return;

    const collaboratoreId = arg.resource?.id;
    if (!this.isSlotAllowed(arg.date, collaboratoreId)) return;

    const now = new Date();
    const effectiveDate = isSameDay(arg.date, now) ? now : arg.date;

    const jsEvent = arg.jsEvent;
    if (!jsEvent) return;
    const item = this.collaboratoriItems().find(
      i => String(i.id) === collaboratoreId
    );
    if (!item) return;
    this.pendingQuickAddSlot = { date: effectiveDate, collaboratoreId };
    this.quickAddMenuOptions.set(this.buildQuickAddOptions(item));
    this.quickAddMenu()?.open({ x: jsEvent.clientX, y: jsEvent.clientY });
  }

  /**
   * Check if a slot click is allowed.
   * @param span The span of the clicked slot.
   */
  private isSelectAllowed(span: DateSpanApi): boolean {
    if (!this.slotClickEnabled()) return false;
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    if (span.end.getTime() - span.start.getTime() > ONE_DAY_MS) return false;
    return this.isSlotAllowed(span.start, span.resource?.id);
  }

  /**
   * Handle slot click on calendar (DEFAULT BEHAVIOR).
   */
  private handleSelect(arg: DateSelectArg): void {
    const collaboratoreId = arg.resource?.id;
    const allowed =
      this.slotClickEnabled() && this.isSlotAllowed(arg.start, collaboratoreId);
    this.calendar()?.getApi()?.unselect();
    if (!allowed || !collaboratoreId) return;

    const now = new Date();
    const effectiveDate = isSameDay(arg.start, now) ? now : arg.start;
    this.slotClick.emit({ date: effectiveDate, collaboratoreId });
  }

  /**
   * Build quick-add options for `item`'s column.
   */
  private buildQuickAddOptions(item: Item): QuickAddMenuOption[] {
    if (item.isAmbulatorio) {
      return [{ action: 'Nuovo turno', icon: '🏢', label: 'Nuovo Turno' }];
    }
    const items = this.operatorEventTypeItems();
    if (!items.length) {
      return [
        { action: 'Assenza', icon: '🚫', label: 'Assenza' },
        { action: 'Riunione', icon: '👥', label: 'Riunione' },
        { action: 'Promemoria', icon: '🔔', label: 'Promemoria' },
      ];
    }
    return items.map(typeItem => ({
      action: typeItem.name,
      icon: typeItem.icon ?? '📌',
      label: typeItem.name,
    }));
  }

  /**
   * Handle quick-add option click.
   */
  protected onQuickAddOptionClick(action: string): void {
    const slot = this.pendingQuickAddSlot;
    this.pendingQuickAddSlot = null;
    if (!slot) return;
    const end = new Date(slot.date.getTime() + 30 * 60 * 1000);
    this.quickAddAction.emit({
      action,
      start: slot.date,
      end,
      collaboratoreId: slot.collaboratoreId,
    });
  }

  /**
   * Store holidays for `info.start` and `info.end` years.
   */
  private handleDatesSet(info: DatesSetArg): void {
    if (!this.showHolidays()) return;
    const startYear = info.start.getFullYear();
    const endYear = new Date(info.end.getTime() - 1).getFullYear();
    [startYear, endYear]
      .filter((year, i, arr) => arr.indexOf(year) === i)
      .filter(year => !this.holidaysService.isYearLoaded(year))
      .forEach(year => {
        this.holidaysService.markYearLoaded(year);
        this.fetchHolidays(year);
      });
  }

  /**
   * Fetch holidays for the given year.
   */
  private fetchHolidays(year: number): void {
    this.holidaysService
      .getHolidays(year, this.holidayCitta())
      .pipe(take(1))
      .subscribe(dates => {
        const calendarApi = this.calendar()?.getApi();
        if (!calendarApi) return;
        this.holidaysService.addHolidays(dates);
        calendarApi.render();
      });
  }

  /**
   * Build resource label.
   */
  private buildResourceLabel(arg: ResourceLabelContentArg): {
    domNodes: HTMLElement[];
  } {
    const item = arg.resource.extendedProps['item'] as Item | undefined;
    const name = item?.name ?? arg.resource.title;
    const tooltip = item?.extraIconLabel ?? '';
    const isAmbulatorio = !!item?.isAmbulatorio;

    const wrapper = document.createElement('div');
    wrapper.className = 'flex items-center gap-2 py-1 pr-2 min-w-0';
    if (tooltip) {
      wrapper.title = tooltip;
      wrapper.setAttribute('aria-label', `${name}, ${tooltip}`);
    }

    const avatar = document.createElement('div');
    avatar.className = isAmbulatorio
      ? 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F0F9FF] text-[#0284C7]'
      : 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-container text-primary';

    const iconEl = document.createElement('span');
    iconEl.className = 'material-icons !text-[18px] leading-none';
    iconEl.textContent = item?.icon ?? 'person';
    iconEl.setAttribute('aria-hidden', 'true');
    avatar.appendChild(iconEl);

    const nameEl = document.createElement('span');

    nameEl.className =
      'min-w-0 whitespace-normal break-words font-semibold text-on-surface';
    nameEl.textContent = name;

    wrapper.append(avatar, nameEl);

    if (item?.extraIcon) {
      const extraIconEl = document.createElement('span');
      extraIconEl.className =
        'material-icons text-on-surface-variant shrink-0 !text-[14px]';
      extraIconEl.textContent = item.extraIcon;
      extraIconEl.title = item.extraIconLabel ?? '';
      wrapper.appendChild(extraIconEl);
    }

    return { domNodes: [wrapper] };
  }

  /**
   * Build event content.
   */
  private buildEventContent(arg: EventContentArg): { domNodes: HTMLElement[] } {
    const classNames = arg.event.classNames;
    const isTurno = classNames.includes('turno-event');
    const nomePaziente = arg.event.extendedProps?.['nomePaziente'] as
      string | undefined;
    const descrizione = arg.event.extendedProps?.['descrizione'] as
      string | undefined;
    const secondaryLine = nomePaziente || descrizione || '';

    const wrapper = document.createElement('div');
    wrapper.className = 'event-content-wrapper';
    const title = isTurno ? this.getTurnoTitle(arg.event) : arg.event.title;
    const isUnresolved = isAppuntamentoUnresolved(arg.event);
    if (isUnresolved) {
      wrapper.classList.add('event-unresolved');
    }
    const hasMatchingPrestazione =
      !!arg.event.extendedProps?.['hasMatchingPrestazione'];
    if (this.focusPrestazione() && !hasMatchingPrestazione) {
      wrapper.classList.add('event-defocused');
    }
    wrapper.title = [
      arg.timeText,
      title,
      secondaryLine,
      isUnresolved ? '⚠️ Nessun esito' : '',
    ]
      .filter(Boolean)
      .join(' — ');

    const tipoEventoId = arg.event.extendedProps?.['tipoEvento']?.id as
      number | undefined;
    if (this.customOptions()?.nonClickableEventTypes?.includes(tipoEventoId)) {
      wrapper.style.cursor = 'default';
    }

    const iconEl = document.createElement('span');
    iconEl.className = 'event-icon';
    iconEl.textContent = classNames.includes('assenza-event')
      ? '🚫'
      : isTurno
        ? this.getTurnoContextIcon()
        : classNames.includes('riunione-event')
          ? '👥'
          : classNames.includes('promemoria-event')
            ? '🔔'
            : classNames.includes('macchinario-event')
              ? '⚙️'
              : '🩺';
    iconEl.setAttribute('aria-hidden', 'true');

    const descriptionEl = document.createElement('div');
    descriptionEl.className = 'event-description';
    descriptionEl.appendChild(iconEl);

    const stato = arg.event.extendedProps?.['stato'] as string | undefined;
    if (stato) {
      const isStatoNonSvolto = arg.event.extendedProps?.['notSvolto'] === false;
      const statoDotEl = document.createElement('span');
      statoDotEl.className = 'event-stato-dot';
      statoDotEl.style.backgroundColor = isStatoNonSvolto
        ? '#dc2626'
        : '#16a34a';
      statoDotEl.title = stato;
      descriptionEl.appendChild(statoDotEl);
    }
    if (isTurno) {
      const turnoTimeEl = document.createElement('span');
      turnoTimeEl.textContent =
        arg.event.start && arg.event.end
          ? formatTurnoTimeRange(arg.event.start, arg.event.end)
          : '';
      descriptionEl.appendChild(turnoTimeEl);
    } else if (arg.timeText) {
      const timeEl = document.createElement('span');
      timeEl.textContent = arg.timeText;
      descriptionEl.appendChild(timeEl);
    }
    wrapper.appendChild(descriptionEl);

    const titleEl = document.createElement('div');

    titleEl.className = 'event-title';
    titleEl.textContent = title;
    wrapper.appendChild(titleEl);

    if (secondaryLine) {
      const secondaryEl = document.createElement('div');
      secondaryEl.className = 'event-secondary';
      secondaryEl.textContent = secondaryLine;
      wrapper.appendChild(secondaryEl);
    }

    return { domNodes: [wrapper] };
  }

  /**
   * Same as `CalendarComponent.getTurnoTitle`: for AMBULATORIO, the turno's
   * own `descrizione` (falling back to the title) instead of the raw title.
   */
  private getTurnoTitle(event: EventContentArg['event']): string {
    if (this.calendarContext() === 'AMBULATORIO') {
      return (event.extendedProps?.['descrizione'] as string) || event.title;
    }
    return event.title;
  }

  /**
   * Turno icon: same as `CalendarComponent.getTurnoContextIcon`
   **/
  private getTurnoContextIcon(): string {
    return this.calendarContext() === 'SANITARIO' ? '🏢' : '👤';
  }

  /**
   * Handles dropping an "attività non assegnata" onto a collaboratore's
   * row/day in the "Timeline Risorse" view.
   */
  private onDrop(info: DropArg): void {
    const resourceId = info.resource?.id;
    const prestazioneId = info.draggedEl.id;
    if (!resourceId || !prestazioneId) return;

    this.assigned.emit({
      prestazioneId,
      resourceId,
      date: info.dateStr,
    });
  }
}
